import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link, useRouter } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Button, CopyId, EmptyState, ErrorState, Table, type Column } from '@bb/ui';
import { ApiError, type ActivityLogChannel, type ActivityLogEntry, type ActivityLogFilters, type ActivityLogType, type AdminApi } from '../../../api/client.ts';
import { PLATFORM_LABELS } from '../push/PushPerformanceTable.tsx';
import { CHANNEL_LABELS } from '../../campaigns/labels.ts';

/** Entries per page; the API allows up to 200. */
export const ACTIVITY_PAGE_SIZE = 50;

export const TYPE_LABELS: Record<ActivityLogType, string> = {
  suppressed: 'Suppressed',
  capped: 'Capped',
  deferred: 'Deferred',
  aborted: 'Aborted',
  failed: 'Failed',
  provider_outcome_unknown: 'Provider outcome unknown',
  token_invalidated: 'Token invalidated',
  unreachable: 'Unreachable',
  card_skipped: 'Card not created',
  card_withdrawn: 'Card withdrawn',
  iam_deferred: 'In-app deferred',
  iam_discarded: 'In-app discarded',
  iam_aborted: 'In-app aborted',
};
const CHANNELS: ActivityLogChannel[] = ['push', 'email', 'sms', 'content_card', 'in_app', 'webhook'];
const DETAIL_LABELS: Record<string, string> = { stage: 'stage', class: 'error class', provider: 'provider', cardId: 'card', origin: 'origin', withdrawReason: 'withdraw reason', placement: 'placement', deliveryChannel: 'delivery channel' };
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

/** `16 Oct 2026, 14:58:04` in the employee's time zone. */
const formatTime = (iso: string) => new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
/** A date input as the start of that day in the employee's time zone (ISO), or the start of the next day for an end. */
const dayStart = (day: string, plus = 0) => {
  const d = new Date(`${day}T00:00:00`);
  d.setDate(d.getDate() + plus);
  return d.toISOString();
};

export interface ActivityLogProps {
  environmentId: string;
  /** A campaign's or Canvas' tab; none — the whole environment. */
  scope?: { campaignId?: string; canvasId?: string };
  heading?: 'h2' | 'h3';
}

interface Draft {
  reason: string;
  profileId: string;
  from: string;
  to: string;
}

/**
 * Message Activity Log (BB-105, AN-011): why messages did not reach people — suppressions, caps, deferrals, aborts,
 * provider errors, invalidated tokens, unreachable recipients — newest first with cursor paging. Each reason code comes
 * with a readable description from the service; test sends are marked. Filters: channel, entry type, reason (code or
 * its base), test sends, profile and dates within the environment's log window.
 */
export function ActivityLog({ environmentId, scope = {}, heading = 'h2' }: ActivityLogProps) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const [channel, setChannel] = useState<ActivityLogChannel | ''>('');
  const [type, setType] = useState<ActivityLogType | ''>('');
  const [tests, setTests] = useState<'all' | 'without' | 'only'>('all');
  const [draft, setDraft] = useState<Draft>({ reason: '', profileId: '', from: '', to: '' });
  const [applied, setApplied] = useState<Draft>(draft);
  const profileIssue = draft.profileId.trim() && !UUID.test(draft.profileId.trim()) ? 'Enter a profile id (UUID).' : null;
  const datesIssue = draft.from && draft.to && draft.from > draft.to ? 'The start date must not be after the end date.' : null;
  const filters: ActivityLogFilters = {
    ...scope,
    ...(channel ? { channel } : {}),
    ...(type ? { type } : {}),
    ...(tests === 'only' ? { isTest: true } : tests === 'without' ? { isTest: false } : {}),
    ...(applied.reason.trim() ? { reason: applied.reason.trim() } : {}),
    ...(applied.profileId.trim() ? { profileId: applied.profileId.trim() } : {}),
    ...(applied.from ? { from: dayStart(applied.from) } : {}),
    ...(applied.to ? { to: dayStart(applied.to, 1) } : {}),
    limit: ACTIVITY_PAGE_SIZE,
  };
  const log = useInfiniteQuery({
    queryKey: ['activity-log', environmentId, filters],
    queryFn: ({ pageParam }) => api.activityLog(environmentId, { ...filters, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });
  const apps = useQuery({ queryKey: ['apps', environmentId], queryFn: () => api.listApps(environmentId), staleTime: 60_000 });
  const appName = (id: string | null) => (id === null ? '—' : (apps.data?.find((a) => a.id === id)?.name ?? id));
  const apply = (e: FormEvent) => {
    e.preventDefault();
    if (!profileIssue && !datesIssue) setApplied(draft);
  };
  const reset = () => {
    const empty = { reason: '', profileId: '', from: '', to: '' };
    setChannel('');
    setType('');
    setTests('all');
    setDraft(empty);
    setApplied(empty);
  };
  const entries = log.data?.pages.flatMap((p) => p.entries) ?? [];
  const logWindow = log.data?.pages[0];
  const columns: Array<Column<ActivityLogEntry>> = [
    { key: 'time', header: 'Time', render: (e) => <time dateTime={e.occurredAt}>{formatTime(e.occurredAt)}</time> },
    { key: 'app', header: 'App', render: (e) => appName(e.appInstanceId) },
    { key: 'channel', header: 'Channel', render: (e) => (e.channel ? `${CHANNEL_LABELS[e.channel]}${e.platform ? ` · ${PLATFORM_LABELS[e.platform] ?? e.platform}` : ''}` : '—') },
    {
      key: 'type',
      header: 'Type',
      render: (e) => (
        <>
          {TYPE_LABELS[e.type] ?? e.type}
          {e.isTest && <span className="tag tag--neutral activity-log__tag">Test send</span>}
        </>
      ),
    },
    { key: 'message', header: 'Message', render: (e) => <MessageCell environmentId={environmentId} entry={e} /> },
    { key: 'reason', header: 'Reason', render: (e) => <ReasonCell entry={e} /> },
    { key: 'profile', header: 'Profile', render: (e) => <CopyId label={`profile id of entry ${e.id}`} value={e.profileId} /> },
  ];
  const Heading = heading;
  return (
    <section className="panel activity-log" aria-label="Message activity log">
      <Heading>Message activity</Heading>
      <p className="hint">Why messages did not reach people: suppressions, caps, deferrals, aborted sends, provider errors and unreachable users. Newest first.</p>
      <form className="filters" role="group" aria-label="Activity filters" onSubmit={apply}>
        <label>
          Channel{' '}
          <select value={channel} onChange={(e) => setChannel(e.target.value as ActivityLogChannel | '')}>
            <option value="">All channels</option>
            {CHANNELS.map((c) => (
              <option key={c} value={c}>
                {CHANNEL_LABELS[c]}
              </option>
            ))}
          </select>
        </label>
        <label>
          Type{' '}
          <select value={type} onChange={(e) => setType(e.target.value as ActivityLogType | '')}>
            <option value="">All types</option>
            {(Object.keys(TYPE_LABELS) as ActivityLogType[]).map((t) => (
              <option key={t} value={t}>
                {TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </label>
        <label>
          Test sends{' '}
          <select value={tests} onChange={(e) => setTests(e.target.value as typeof tests)}>
            <option value="all">Included</option>
            <option value="without">Left out</option>
            <option value="only">Only test sends</option>
          </select>
        </label>
        <label>
          Reason code <input value={draft.reason} placeholder="frequency_cap" spellCheck={false} onChange={(e) => setDraft({ ...draft, reason: e.target.value })} />
        </label>
        <label>
          Profile id <input value={draft.profileId} spellCheck={false} aria-invalid={profileIssue ? true : undefined} onChange={(e) => setDraft({ ...draft, profileId: e.target.value })} />
        </label>
        <label>
          From <input type="date" value={draft.from} max={draft.to || undefined} onChange={(e) => setDraft({ ...draft, from: e.target.value })} />
        </label>
        <label>
          To <input type="date" value={draft.to} min={draft.from || undefined} onChange={(e) => setDraft({ ...draft, to: e.target.value })} />
        </label>
        <Button type="submit" size="sm">
          Apply
        </Button>
        <Button size="sm" variant="ghost" onClick={reset}>
          Clear filters
        </Button>
        {(profileIssue ?? datesIssue) && (
          <span role="alert" className="field-error">
            {profileIssue ?? datesIssue}
          </span>
        )}
      </form>
      {logWindow && (
        <p className="hint" aria-label="Log window">
          The log keeps {logWindow.retentionDays} days: showing {formatTime(logWindow.from)} – {formatTime(logWindow.to)}. A reason code matches itself and its parameters (frequency_cap finds every cap rule).
        </p>
      )}
      {log.isError ? (
        <ErrorState title="Message activity could not be loaded" message={errorText(log.error)} {...(log.error instanceof ApiError ? { code: log.error.code } : {})} onRetry={() => void log.refetch()} />
      ) : !log.data ? (
        <p aria-busy="true">Loading message activity…</p>
      ) : (
        <Table
          caption="Message activity"
          columns={columns}
          rows={entries}
          rowKey={(e) => e.id}
          hasMore={log.hasNextPage}
          loadingMore={log.isFetchingNextPage}
          onLoadMore={() => void log.fetchNextPage()}
          empty={<EmptyState title="Nothing to show" description="No message was held back, dropped or failed with these filters in the log window." />}
        />
      )}
    </section>
  );
}

/** What was sent: the campaign (a link to it) or the Canvas and its step, with the variant; a test send says so. */
function MessageCell({ environmentId, entry: e }: { environmentId: string; entry: ActivityLogEntry }) {
  const variant = e.variantApiId ? <span className="push-table__rate">variant {e.variantApiId}</span> : null;
  if (e.campaign)
    return (
      <>
        <Link to="/env/$environmentId/campaigns/$campaignId" params={{ environmentId, campaignId: e.campaign.id }}>
          {e.campaign.name ?? e.campaign.id}
        </Link>
        {variant}
      </>
    );
  if (e.canvas)
    return (
      <>
        <Link to="/env/$environmentId/canvas/$canvasId" params={{ environmentId, canvasId: e.canvas.id }}>
          {e.canvas.name ?? e.canvas.id}
        </Link>
        {e.canvas.stepId && <span className="push-table__rate">step {e.canvas.stepId}</span>}
        {variant}
      </>
    );
  return <>{e.source === 'test' ? 'Test send' : '—'}</>;
}

/** The reason in words, its code, and the safe diagnostic detail (stage, provider, …); the dispatch to trace it. */
function ReasonCell({ entry: e }: { entry: ActivityLogEntry }) {
  const detail = Object.entries(e.detail);
  return (
    <>
      <span className="push-table__count">{e.reason?.description.en ?? (e.reasonCode ? e.reasonCode : 'No reason recorded')}</span>
      {e.reasonCode && <code className="push-table__rate">{e.reasonCode}</code>}
      {detail.length > 0 && (
        <span className="push-table__rate">
          {detail.map(([k, v]) => `${DETAIL_LABELS[k] ?? k}: ${String(v)}`).join(' · ')}
        </span>
      )}
      {e.dispatchId && (
        <span className="push-table__rate" title="Dispatch id for tracing this send">
          dispatch {e.dispatchId}
        </span>
      )}
    </>
  );
}
